/**
 * Frontend types - aligned with backend API
 */

// Review status enum - matches backend ClauseReviewStatus
export enum ReviewStatus {
  PENDING = 'pending',
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non_compliant',
  UNCERTAIN = 'uncertain',
  IGNORED = 'ignored',
  EXEMPTED = 'exempted'
}

// Project status enum - matches backend ProjectStatus
export enum ProjectStatus {
  CREATED = 'created',
  FILES_UPLOADED = 'files_uploaded',
  PARSING = 'parsing',
  PARSED = 'parsed',
  REVIEWING = 'reviewing',
  REVIEWED = 'reviewed',
  REPORT_GENERATING = 'report_generating',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// File type enum
export enum FileType {
  CONTRACT = 'contract',
  STANDARD = 'standard',
  EXCEPTION = 'exception'
}

// File status enum
export enum FileStatus {
  UPLOADED = 'uploaded',
  PARSING = 'parsing',
  PARSED = 'parsed',
  FAILED = 'failed'
}

// Exemption type enum
export enum ExemptionType {
  KEYWORD = 'keyword',
  CLAUSE_NUMBER = 'clause_number',
  CLAUSE_ID = 'clause_id',
  TEXT_MATCH = 'text_match'
}

// Severity level enum
export enum SeverityLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// User interface
export interface User {
  id: string;
  tpid: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

// Clause interface - matches backend Clause model
export interface Clause {
  id: string;
  sequence: number;
  clause_no: string | null;
  title: string | null;
  content: string;
  party_tags: string[] | null;
  source_page: number[] | null;
  version: number;
  clause_type: 'main_body' | 'supplementary';
  source_span?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  
  // Review result (joined from ReviewResult)
  review?: ReviewResult;
  
  // User edit (joined from UserEdit)
  user_edit?: UserEdit;
}

// Review result interface
export interface ReviewResult {
  id: string;
  clause_id: string;
  status: ReviewStatus;
  evidence: EvidenceItem[] | null;
  issues: string[] | null;
  suggested_cn: string | null;
  suggested_en: string | null;
  confidence: number | null;
  rationale: string | null;
  uncertain_reasons: string[] | null;
  missing_info: string[] | null;
  matched_rules: string[] | null;
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

// Evidence item from AI review
export interface EvidenceItem {
  rule_id: string;
  rule_title: string;
  matched_text: string;
  relevance_score: number;
}

// User edit interface
export interface UserEdit {
  id: string;
  clause_id: string;
  user_id: string;
  edit_lines: string[];
  final_text: string | null;
  notes: string | null;
  version: number;
  created_at: string;
}

// Project interface
export interface Project {
  id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  user_prompt: string | null;
  error_message: string | null;
  clause_version: number;
  created_at: string;
  updated_at: string;
  
  // Extended fields
  file_count?: number;
  clause_count?: number;
  review_progress?: number;
}

// Uploaded file interface
export interface UploadedFile {
  id: string;
  project_id: string;
  file_type: FileType;
  original_name: string;
  storage_path?: string;
  mime_type: string;
  file_size: number;
  status: FileStatus;
  parsed_content?: string | null;
  metadata?: Record<string, unknown> | null;
  error_message?: string | null;
  checksum?: string;
  created_at: string;
}

// Exemption rule interface
export interface ExemptionRule {
  id: string;
  project_id?: string;
  exemption_type: ExemptionType;
  name: string;
  keywords: string[] | null;
  clause_numbers: string[] | null;
  clause_ids: string[] | null;
  text_patterns: string[] | null;
  reason: string | null;
  source_file_id?: string | null;
  created_at: string;
}

// Report interface
export interface Report {
  id: string;
  project_id: string;
  version: number;
  total_clauses: number;
  compliant_count: number;
  non_compliant_count: number;
  uncertain_count: number;
  ignored_count: number;
  exempted_count: number;
  non_compliant_items: NonCompliantItem[];
  summary?: Record<string, unknown> | null;
  created_at: string;
}

// Non-compliant item in report
export interface NonCompliantItem {
  clause_id: string;
  clause_no: string | null;
  original_text: string;
  issues: string[];
  evidence: EvidenceItem[];
  suggested_cn: string | null;
  suggested_en: string | null;
  user_edit: string | null;
  confidence: number | null;
}

// Export job interface
export interface ExportJob {
  id: string;
  project_id: string;
  format: 'docx' | 'pdf';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  current_stage: string | null;
  output_path?: string | null;
  download_url: string | null;
  version: number;
  options?: ExportOptions | null;
  error_message: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  expires_at: string | null;
  created_at: string;
}

// Export options
export interface ExportOptions {
  comparison_mode: 'single' | 'dual_column' | 'track_changes';
  include_appendix: boolean;
  include_summary: boolean;
  user_edit_priority: boolean;
}

// Review standard interface
export interface ReviewStandard {
  id: string;
  name: string;
  description: string | null;
  version: number;
  is_active: boolean;
  is_default: boolean;
  rule_count?: number;
  created_at: string;
  updated_at: string;
  rules?: StandardRule[];
}

// Standard rule interface
export interface StandardRule {
  id: string;
  standard_id: string;
  rule_id: string;
  title: string;
  description: string;
  scope: string | null;
  severity: SeverityLevel;
  examples: Array<{ type: string; description: string }> | null;
  keywords: string[] | null;
  sequence: number;
  created_at: string;
}

// Upload type alias
export type UploadType = 'contract' | 'standard' | 'exception';

// Helper function to map backend status to display text
export const getStatusDisplayText = (status: ReviewStatus): string => {
  const map: Record<ReviewStatus, string> = {
    [ReviewStatus.PENDING]: '待审核',
    [ReviewStatus.COMPLIANT]: '合规',
    [ReviewStatus.NON_COMPLIANT]: '不合规',
    [ReviewStatus.UNCERTAIN]: '不确定',
    [ReviewStatus.IGNORED]: '已忽略',
    [ReviewStatus.EXEMPTED]: '已豁免',
  };
  return map[status] || status;
};

// Helper function to map project status to display text
export const getProjectStatusDisplayText = (status: ProjectStatus): string => {
  const map: Record<ProjectStatus, string> = {
    [ProjectStatus.CREATED]: '已创建',
    [ProjectStatus.FILES_UPLOADED]: '文件已上传',
    [ProjectStatus.PARSING]: '解析中',
    [ProjectStatus.PARSED]: '已解析',
    [ProjectStatus.REVIEWING]: '审核中',
    [ProjectStatus.REVIEWED]: '已审核',
    [ProjectStatus.REPORT_GENERATING]: '生成报告中',
    [ProjectStatus.COMPLETED]: '已完成',
    [ProjectStatus.FAILED]: '失败',
  };
  return map[status] || status;
};

// Helper to get risk level from confidence
export const getRiskLevel = (status: ReviewStatus, confidence?: number | null): 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE' => {
  if (status === ReviewStatus.COMPLIANT || status === ReviewStatus.EXEMPTED) {
    return 'NONE';
  }
  if (status === ReviewStatus.NON_COMPLIANT) {
    return 'HIGH';
  }
  if (status === ReviewStatus.UNCERTAIN) {
    if (confidence && confidence < 0.5) return 'HIGH';
    if (confidence && confidence < 0.7) return 'MEDIUM';
    return 'LOW';
  }
  return 'NONE';
};
